import { useCallback, useEffect, useRef, useState } from "react";
import { MousePointerClick } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useChartStore } from "@/stores/chartStore";

interface ReplayStartMenuProps {
  children: React.ReactNode;
  onConfirm: (epochMs: number) => void;
}

function epochToDatetimeLocal(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function ReplayStartMenu({ children, onConfirm }: ReplayStartMenuProps) {
  const [open, setOpen] = useState(false);

  const { oldestEpoch, latestEpoch } = useChartStore(
    useShallow((s) => ({ oldestEpoch: s.oldestEpoch, latestEpoch: s.latestEpoch })),
  );

  const [inputValue, setInputValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setError(null);
      return;
    }
    if (latestEpoch != null) {
      setInputValue(epochToDatetimeLocal(latestEpoch));
    } else {
      setInputValue("");
    }
    const id = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(id);
  }, [open, latestEpoch]);

  const handleConfirm = useCallback(() => {
    if (!inputValue) {
      setError("Pick a date to start replay.");
      return;
    }
    const ms = new Date(inputValue).getTime();
    if (Number.isNaN(ms)) {
      setError("Invalid date.");
      return;
    }
    if (oldestEpoch != null && ms < oldestEpoch) {
      const d = new Date(oldestEpoch);
      setError(`No data before ${d.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" })}`);
      return;
    }
    if (latestEpoch != null && ms > latestEpoch) {
      const d = new Date(latestEpoch);
      setError(`Beyond available data (up to ${d.toLocaleDateString(undefined, { month: "short", day: "2-digit", year: "numeric" })})`);
      return;
    }
    onConfirm(ms);
    setOpen(false);
  }, [inputValue, oldestEpoch, latestEpoch, onConfirm]);

  const body = (
    <div className="flex flex-col gap-0">
      {/* Tip */}
      <div
        className="flex items-center gap-2 px-4 py-3"
        style={{ borderBottom: "1px solid var(--lyt-border-default)" }}
      >
        <MousePointerClick className="size-3.5 shrink-0 text-muted-foreground" />
        <span className="text-xs text-muted-foreground leading-snug">
          Right-click any candle to begin from there
        </span>
      </div>

      {/* Datetime input */}
      <div className="px-4 py-3">
        <input
          ref={inputRef}
          type="datetime-local"
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleConfirm();
          }}
          className="h-8 w-full rounded-md border px-2 text-xs font-mono-tabular focus:outline-none focus:ring-1 focus:ring-golden/40"
          style={{
            backgroundColor: "var(--bg-card)",
            borderColor: "var(--lyt-border-default)",
            color: "var(--text-primary)",
            colorScheme: "dark",
          }}
          max={latestEpoch != null ? epochToDatetimeLocal(latestEpoch) : undefined}
          min={oldestEpoch != null ? epochToDatetimeLocal(oldestEpoch) : undefined}
        />
      </div>

      {/* Validation error */}
      {error && (
        <div className="px-4 pb-2">
          <p className="text-xs text-loss leading-tight">{error}</p>
        </div>
      )}

      {/* Footer */}
      <div
        className="flex items-center justify-between gap-3 px-4 py-3"
        style={{ borderTop: "1px solid var(--lyt-border-default)" }}
      >
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          className="flex items-center justify-center h-7 px-4 rounded-full bg-golden text-background text-xs font-bold transition-transform hover:scale-[1.02] active:scale-[0.98]"
        >
          Start
        </button>
      </div>
    </div>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="end"
        side="bottom"
        sideOffset={8}
        className="w-[280px] p-0 shadow-[0_8px_40px_rgba(0,0,0,0.55)]"
        style={{
          backgroundColor: "var(--bg-elevated)",
          borderColor: "var(--lyt-border-default)",
          borderRadius: "var(--radius-lg, 12px)",
        }}
      >
        <div
          className="flex items-center gap-2 px-4 pt-3.5 pb-3"
          style={{ borderBottom: "1px solid var(--lyt-border-default)" }}
        >
          <span className="text-xs font-semibold text-foreground">Start replay</span>
        </div>
        {body}
      </PopoverContent>
    </Popover>
  );
}
